import { useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { Modal } from '@mui/material';
import { useEffect, useState } from 'react';
import { getClients } from '~/api/clients';
import { createPushNotifications } from '~/api/notifications/index.ts';
import { useAuth } from '~/components/auth/AuthProvider.tsx';
import { ActionButton } from '~/components/common/ActionButton';
import { ClientType } from '~/types/resources/ClientType';
import { NotificationForm, NotificationParams } from '~/types/resources/Notification';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '~/types/table/ActionTableSortParamsType.ts';
import { getPushNotificationLink } from '~/utils/getLinks';
import { CreateForm } from './CreateForm';
import styles from './styles.module.scss';

const MAX_LIMIT_COUNT = '300';

export const CreatePushNotification = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fetchClientsData, setFetchClientsData] = useState<ClientType[]>([]);
  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>(
    defaultActionTableSortParams
  );
  const [totalCount, setTotalCount] = useState<number>(0);
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<NotificationForm | null>(null);
  const [errMsg, setErrMsg] = useState<string>('');
  // PEA-365: 後続の企業名検索・ページングでは選択済みの企業名を一覧の外に表示する必要があり、
  // ページを移動すると fetchClientsData から消えて ID から名前を引けなくなる。
  // そのため選択状態は ID ではなく企業そのものを保持する
  const [selectedClients, setSelectedClients] = useState<ClientType[]>([]);
  const [selectAllState, setSelectAllState] = useState<true | false | 'indeterminate'>(false);

  const getClientData = async () => {
    try {
      // アクティブなパートナー企業のみを取得する
      const { data, headers } = await getClients(signOut, { limit: MAX_LIMIT_COUNT, enabled: '1' });
      setTotalCount(Number(headers['total-count']));
      const clientsData = data.clients;
      setFetchClientsData(clientsData);
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = (data: NotificationForm) => {
    setFormData({ ...data, target_client_ids: selectedClients.map(client => client.id) });
    setIsOpen(true);
  };

  const omit = <T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
    const ret = { ...obj };
    keys.forEach(key => {
      delete ret[key];
    });
    return ret;
  };

  const onSubmit = async () => {
    try {
      // 手動選択の場合のみ、対象企業のチェックを行う
      if (formData?.deliveryTarget === 'manual' && formData?.target_client_ids.length === 0) {
        setErrMsg('対象企業が選択されていません');
        return;
      }

      // グループ選択の場合、グループが選択されているかチェック
      if (formData?.deliveryTarget === 'group' && !formData?.selectedGroupId) {
        setErrMsg('グループが選択されていません');
        return;
      }

      // immediateの場合はscheduled_datetimeをnullにする
      const scheduled_datetime =
        formData.isImmediate === 'scheduled' && formData.selectedDate && formData.selectedTime
          ? `${formData.selectedDate}T${String(formData.selectedTime).padStart(2, '0')}:${formData.selectedMinute}:00`
          : null;

      const rest = omit(formData, [
        'selectedDate',
        'selectedTime',
        'selectedMinute',
        'target_client_ids',
        'isImmediate',
        'deliveryTarget',
        'selectedGroupId',
        'excludedTargets',
      ]);

      const payload: NotificationParams = {
        ...rest,
        scheduled_datetime,
        category: 'none',
        selected_option: formData.isImmediate,
        ...(formData.excludedTargets?.includes('family_users') && {
          target_user_type: 'employees_only',
        }),
      };

      // 配信先に応じてgroup_idまたはtarget_client_idsを設定
      if (formData.deliveryTarget === 'group' && formData.selectedGroupId) {
        payload.group_id = Number(formData.selectedGroupId);
      } else if (formData.deliveryTarget === 'manual' && formData.target_client_ids?.length > 0) {
        payload.target_client_ids = formData.target_client_ids;
      }
      // 'all'の場合はgroup_idもtarget_client_idsも送らない

      await createPushNotifications(payload);
      navigate(getPushNotificationLink());
    } catch (error) {
      if (error) {
        setErrMsg(error.details?.join(', '));
      } else {
        setErrMsg('An unexpected error occurred while creating the notification.');
      }
    }
  };

  useEffect(() => {
    getClientData();
  }, []);

  useEffect(() => {
    if (selectAllState === true) {
      // fetchClientsData をそのまま渡すと 2 つの state が同じ配列を参照してしまう。
      // 一覧側を破壊的に更新した際に選択状態が道連れで変わるのを避けるためコピーする
      setSelectedClients([...fetchClientsData]);
    } else if (selectAllState === false) {
      setSelectedClients([]);
    }
  }, [selectAllState, fetchClientsData]);

  // 個別選択で全てのチェックボックスが選択された場合、全件選択チェックボックスもチェックにするための制御
  // 依存配列には selectedClients（state）をそのまま並べること。
  // ID 配列を派生させて依存配列に入れると毎レンダーで参照が変わり、この effect が毎回走る
  useEffect(() => {
    if (selectAllState === 'indeterminate' && selectedClients.length === totalCount) {
      setSelectAllState(true);
    }
  }, [selectedClients, selectAllState, totalCount]);

  return (
    <div className={styles.Root} data-testid='client-page'>
      <CreateForm
        fetchClientsData={fetchClientsData}
        setFetchClientsData={setFetchClientsData}
        sortParams={sortParams}
        setSortParams={setSortParams}
        isLoading={isLoading}
        handleFormSubmit={handleFormSubmit}
        selectedClients={selectedClients}
        setSelectedClients={setSelectedClients}
        selectAllState={selectAllState}
        setSelectAllState={setSelectAllState}
      />
      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        <div className={styles.modalContent}>
          <div className={styles.errorMessage} data-testid='edit-error-messages'>
            {errMsg}
          </div>
          <div className={styles.modalDescription}>
            {formData?.deliveryTarget === 'all' && 'すべての企業に送信されます。'}
            {formData?.deliveryTarget === 'group' && 'グループに所属する企業に送信されます。'}
            {formData?.deliveryTarget === 'manual' &&
              (formData.target_client_ids.length > 0
                ? `${formData.target_client_ids.length}件の企業に送信されます。`
                : '対象企業が選択されていません。')}
            <br />
            {formData &&
              (formData.deliveryTarget !== 'manual' || formData.target_client_ids.length > 0) &&
              'この内容で送信してもよろしいですか？'}
          </div>
          <div className={styles.btnContainer}>
            <ActionButton
              content='キャンセル'
              className={styles.addBtn}
              action={() => setIsOpen(false)}
            />
            <ActionButton
              content='送信'
              type='dark'
              className={styles.addBtn}
              action={onSubmit}
              disable={
                !formData ||
                (formData.deliveryTarget === 'manual' && formData.target_client_ids.length === 0) ||
                (formData.deliveryTarget === 'group' && !formData.selectedGroupId)
              }
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};
